import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import {
  User,
  Mail,
  Phone,
  Code,
  Shield,
  LogOut,
  ChevronRight,
  Save,
  Headphones,
  Camera,
} from 'lucide-react';

const initialForm = {
  type: 'BANK', // or 'UPI'
  // BANK fields
  accountHolderName: '',
  bankName: '',
  bankBranch: '',
  accountNumber: '',
  ifscCode: '',
  // UPI fields
  upiId: '',
};

const BANK_DOC_KEYS = ['bank_passbook', 'cancelled_cheque', 'bank_statement'];
const UPI_DOC_KEYS = ['upi_screenshot'];

const initialDocFilesState = {
  bank_passbook: null,
  cancelled_cheque: null,
  bank_statement: null,
  upi_screenshot: null,
};

const initialDocProgressState = {
  bank_passbook: { uploaded: 0, total: 0, status: 'idle' },
  cancelled_cheque: { uploaded: 0, total: 0, status: 'idle' },
  bank_statement: { uploaded: 0, total: 0, status: 'idle' },
  upi_screenshot: { uploaded: 0, total: 0, status: 'idle' },
};

const MAX_DOC_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPTED_DOC_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // store array of beneficiaries returned by backend
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // beneficiary documents (for BANK/UPI)
  const [docFiles, setDocFiles] = useState(initialDocFilesState);
  const [docPreviews, setDocPreviews] = useState({});
  const [docProgress, setDocProgress] = useState(initialDocProgressState);
  const docObjectUrlsRef = useRef(new Set());

  // Refs for focusing inputs on server-side validation errors
  const nameRef = useRef(null);
  const bankNameRef = useRef(null);
  const bankBranchRef = useRef(null);
  const accountNumberRef = useRef(null);
  const ifscRef = useRef(null);
  const upiRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchBeneficiary(), fetchReferrerInfo()]);
      setLoading(false);
    })();
  }, []);

  // cleanup object URLs for beneficiary doc previews
  useEffect(() => {
    return () => {
      docObjectUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      });
      docObjectUrlsRef.current.clear();
    };
  }, []);

  const fetchReferrerInfo = async () => {
    try {
      const res = await api.get('/users/referrer');
      if (res?.data) setReferrerInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch referrer:', err?.response?.data || err);
    }
  };

  /**
   * GET /bank-details returns either:
   * - array of BeneficiaryResponse
   *
   * Normalize to array and map each item to the UI shape used in this component.
   */
  const fetchBeneficiary = async () => {
    try {
      const res = await api.get('/bank-details');
      const backend = res?.data ?? [];

      const list = Array.isArray(backend) ? backend : [backend];

      const mappedList = list.map((b) => {
        const t = (b.type || 'BANK').toUpperCase();
        if (t === 'UPI') {
          return {
            bankdetailId: b.bankdetailId || b.id || null,
            type: 'UPI',
            accountHolderName: b.name || '',
            upiId: b.upiId || '',
            documentation: b.documentation || null,
            status: b.status || 'Pending',
            verified: !!b.verified,
          };
        } else {
          return {
            bankdetailId: b.bankdetailId || b.id || null,
            type: 'BANK',
            accountHolderName: b.name || '',
            bankName: b.bankName || '',
            bankBranch: b.branchName || '',
            accountNumber: b.accountNumber || '',
            ifscCode: b.ifsc || b.ifscCode || '',
            documentation: b.documentation || null,
            status: b.status || 'Pending',
            verified: !!b.verified,
          };
        }
      });

      setBeneficiaries(mappedList);

      // if exactly one item, populate the form with it (old UX)
      if (mappedList.length === 1) {
        setForm({ ...initialForm, ...mappedList[0] });
      } else {
        setForm(initialForm);
      }
    } catch (err) {
      console.error('fetchBeneficiary error:', err?.response?.data || err);
      toast.error('Unable to load beneficiary');
      setBeneficiaries([]);
      setForm(initialForm);
    }
  };

  const validateForm = (f) => {
    if (!f.type) return 'Select beneficiary type';
    if (f.type === 'BANK') {
      if (!f.accountHolderName.trim()) return 'Account holder name required';
      if (!f.bankName.trim()) return 'Bank name required';
      if (!f.accountNumber.trim()) return 'Account number required';
      if (!f.ifscCode.trim() || f.ifscCode.trim().length < 6) return 'IFSC looks invalid';
      return null;
    } else {
      if (!f.accountHolderName.trim()) return 'Name is required for UPI';
      if (!f.upiId.trim()) return 'UPI ID is required';
      if (!f.upiId.includes('@') || f.upiId.length < 3) return 'UPI ID looks invalid';
      return null;
    }
  };

  // Helper: focus the related input based on server 'field'
  const focusField = (field) => {
    if (!field) return;
    const mapping = {
      name: nameRef,
      accountHolderName: nameRef,
      bankName: bankNameRef,
      bankBranch: bankBranchRef,
      accountNumber: accountNumberRef,
      ifsc: ifscRef,
      ifscCode: ifscRef,
      upiId: upiRef,
      upi: upiRef,
    };
    const ref = mapping[field];
    if (ref && ref.current && typeof ref.current.focus === 'function') {
      ref.current.focus();
    }
  };

  // ---------- Beneficiary doc uploads (KYC-style) ----------

  const handleDocFilePicked = (name, file) => {
    if (!file) return;

    if (
      !ACCEPTED_DOC_TYPES.includes(file.type) &&
      !file.type.startsWith('image/')
    ) {
      toast.error('Unsupported file type. Use JPG/PNG/WebP images.');
      return;
    }
    if (file.size > MAX_DOC_BYTES) {
      toast.error(`${name} is too large. Max 10MB allowed.`);
      return;
    }

    setDocFiles((prev) => ({ ...prev, [name]: file }));

    // revoke old preview if present
    if (docPreviews[name]) {
      try {
        URL.revokeObjectURL(docPreviews[name]);
        docObjectUrlsRef.current.delete(docPreviews[name]);
      } catch (e) {}
    }

    const url = URL.createObjectURL(file);
    docObjectUrlsRef.current.add(url);
    setDocPreviews((prev) => ({ ...prev, [name]: url }));
    setDocProgress((prev) => ({
      ...prev,
      [name]: { uploaded: 0, total: file.size, status: 'idle' },
    }));
  };

  const handleDocChange = (name) => (e) => {
    const file = e.target.files && e.target.files[0];
    handleDocFilePicked(name, file);
  };

  const handleDocDrop = (name) => (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    handleDocFilePicked(name, file);
  };

  const removeDocFile = (name) => {
    setDocFiles((prev) => ({ ...prev, [name]: null }));
    setDocProgress((prev) => ({
      ...prev,
      [name]: { uploaded: 0, total: 0, status: 'idle' },
    }));
    if (docPreviews[name]) {
      try {
        URL.revokeObjectURL(docPreviews[name]);
        docObjectUrlsRef.current.delete(docPreviews[name]);
      } catch (e) {}
      setDocPreviews((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const uploadFileToPresignedUrl = async (putUrl, file, name, extraHeaders = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', putUrl, true);

      try {
        xhr.setRequestHeader(
          'Content-Type',
          file.type || 'application/octet-stream'
        );
      } catch (e) {}

      for (const h in extraHeaders) {
        try {
          xhr.setRequestHeader(h, extraHeaders[h]);
        } catch (e) {}
      }

      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          setDocProgress((prev) => ({
            ...prev,
            [name]: {
              ...prev[name],
              uploaded: ev.loaded,
              total: ev.total,
              status: 'uploading',
            },
          }));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setDocProgress((prev) => ({
            ...prev,
            [name]: {
              ...prev[name],
              uploaded: prev[name].total,
              status: 'done',
            },
          }));
          resolve();
        } else {
          setDocProgress((prev) => ({
            ...prev,
            [name]: { ...prev[name], status: 'error' },
          }));
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        setDocProgress((prev) => ({
          ...prev,
          [name]: { ...prev[name], status: 'error' },
        }));
        reject(new Error('Network error during upload'));
      };

      xhr.send(file);
    });
  };

  const uploadBeneficiaryDocsIfAny = async () => {
    const keysToUpload = form.type === 'BANK' ? BANK_DOC_KEYS : UPI_DOC_KEYS;

    const actualKeys = keysToUpload.filter((k) => docFiles[k]);
    if (actualKeys.length === 0) {
      // No documents selected; nothing to upload
      return null;
    }

    const payload = {
      files: actualKeys.map((key) => {
        const f = docFiles[key];
        return {
          name: key,
          filename: f.name,
          content_type: f.type || 'image/jpeg',
        };
      }),
    };

    const resp = await api.post('/bank-details/presign', payload);
    const uploadInfo = resp.data?.uploads || [];

    if (!Array.isArray(uploadInfo) || uploadInfo.length !== actualKeys.length) {
      throw new Error('Invalid presign response from server');
    }

    const uploads = uploadInfo.map((info, idx) => {
      const name = actualKeys[idx];
      const file = docFiles[name];
      const extraHeaders = info.signed_headers || {};
      return uploadFileToPresignedUrl(info.put_url, file, name, extraHeaders).then(
        () => ({
          name,
          public_url: info.public_url,
          key: info.key,
        })
      );
    });

    const results = await Promise.all(uploads);
    const urlsMap = {};
    results.forEach((r) => {
      urlsMap[r.name] = r.public_url;
    });
    return urlsMap; // matches BeneficiaryDocUrls field names
  };

  const handleSave = async () => {
    const errMsg = validateForm(form);
    if (errMsg) {
      toast.error(errMsg);
      return;
    }

    setSaving(true);
    try {
      // 1) Upload documents (if any selected)
      let documentation = null;
      try {
        const urlsMap = await uploadBeneficiaryDocsIfAny();
        if (urlsMap) {
          documentation = urlsMap; // keys: bank_passbook, cancelled_cheque, bank_statement, upi_screenshot
        }
      } catch (uploadErr) {
        console.error('Beneficiary doc upload error:', uploadErr);
        toast.error(uploadErr.message || 'Failed to upload documents');
        setSaving(false);
        return;
      }

      // 2) Build payload
      let payload;
      if (form.type === 'BANK') {
        payload = {
          type: 'BANK',
          name: form.accountHolderName.trim(),
          accountNumber: form.accountNumber.trim(),
          ifsc: form.ifscCode.trim().toUpperCase(),
          bankName: form.bankName.trim(),
          branchName: form.bankBranch.trim(),
        };
      } else {
        payload = {
          type: 'UPI',
          name: form.accountHolderName.trim(),
          upiId: form.upiId.trim().toLowerCase(),
        };
      }

      if (documentation) {
        payload.documentation = documentation;
      }

      await api.post('/bank-details', payload);
      toast.success('Beneficiary saved');

      // reset doc state after successful save
      setDocFiles(initialDocFilesState);
      setDocProgress(initialDocProgressState);
      Object.values(docPreviews).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
          docObjectUrlsRef.current.delete(url);
        } catch (e) {}
      });
      setDocPreviews({});

      // Refresh full list (backend upserts by type; frontend mirrors server)
      await fetchBeneficiary();
      setShowForm(false);
    } catch (err) {
      console.error('save beneficiary error:', err?.response?.data || err);

      const data = err?.response?.data;
      const serverMsg =
        data?.detail?.message ||
        data?.message ||
        (typeof data === 'string' ? data : null);

      if (serverMsg) {
        toast.error(String(serverMsg));
      } else {
        toast.error('Failed to save beneficiary');
      }

      const errorField =
        data?.field || (data?.detail && data?.detail?.field) || null;
      if (errorField) {
        setTimeout(() => focusField(errorField), 50);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      toast.success('Logged out');
    }
  };

  // Small inline upload card for beneficiary docs (based on KYC UploadCard)
  const DocUploadCard = ({ keyName, label }) => {
    const info = docProgress[keyName] || { uploaded: 0, total: 0, status: 'idle' };
    const pct =
      info.total > 0 ? Math.round((info.uploaded / info.total) * 100) : 0;
    const status = info.status || 'idle';

    let statusText = 'Ready';
    if (status === 'uploading') statusText = `Uploading ${pct}%`;
    else if (status === 'done') statusText = 'Uploaded';
    else if (status === 'error') statusText = 'Error';

    return (
      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDocDrop(keyName)}
        className="group relative flex flex-col gap-3 p-3 rounded-xl border border-gray-200 bg-white shadow-sm"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 text-xs font-bold">
              {label.charAt(0)}
            </div>
            <div>
              <div className="text-xs font-medium">{label}</div>
              <div className="text-[11px] text-gray-500">
                Clear photo, JPG/PNG/WebP
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleDocChange(keyName)}
              className="hidden"
              id={`benef-doc-${keyName}`}
            />
            {docPreviews[keyName] ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeDocFile(keyName);
                }}
                className="text-[11px] px-2 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100"
              >
                Remove
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const el = document.getElementById(`benef-doc-${keyName}`);
                  if (el) el.click();
                }}
                className="text-[11px] px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
              >
                Choose
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="rounded-lg border-dashed p-2 min-h-[60px] flex items-center justify-center bg-gray-50">
              {docPreviews[keyName] ? (
                <img
                  src={docPreviews[keyName]}
                  alt={label}
                  className="max-h-24 object-contain rounded-md"
                />
              ) : (
                <div className="text-center">
                  <Camera className="mx-auto h-6 w-6 text-gray-300" />
                  <div className="text-[11px] text-gray-500 mt-1">
                    Drag & drop or{' '}
                    <button
                      type="button"
                      onClick={() =>
                        document
                          .getElementById(`benef-doc-${keyName}`)
                          ?.click()
                      }
                      className="underline"
                    >
                      browse
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="flex-1">
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    style={{ width: `${pct}%` }}
                    className={`h-1.5 rounded-full ${
                      status === 'error' ? 'bg-red-500' : 'bg-primary'
                    }`}
                  />
                </div>
              </div>
              <div className="text-[10px] text-gray-500 w-16 text-right">
                {statusText}
              </div>
            </div>
          </div>

          <div className="w-16 text-right hidden sm:block">
            <div className="text-[11px] text-gray-500">Preview</div>
            {docPreviews[keyName] ? (
              <img
                src={docPreviews[keyName]}
                alt="preview"
                className="mt-1 w-14 h-14 object-cover rounded-lg border border-gray-100"
              />
            ) : (
              <div className="mt-1 w-14 h-14 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-[11px] text-gray-300">
                No file
              </div>
            )}
          </div>
        </div>
      </label>
    );
  };

  return (
    <MobileLayout>
      <Toaster position="top-right" />
      <div className="p-4 space-y-4">
        {/* Profile Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-6 text-white text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <User className="w-10 h-10" />
          </div>
          <h2 className="text-xl font-heading font-bold">
            {user?.name || '—'}
          </h2>
          <p className="text-sm opacity-90 mt-1">{user?.email || '—'}</p>
        </div>

        {/* Basic info cards */}
        <div className="space-y-3">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-light rounded-lg">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Email</p>
                <p className="text-sm font-semibold text-gray-900">
                  {user?.email || 'Not provided'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success-light rounded-lg">
                <Phone className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Phone</p>
                <p className="text-sm font-semibold text-gray-900">
                  {user?.phone || 'Not provided'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning-light rounded-lg">
                <Code className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-xs text-gray-600">Referral Code</p>
                <p className="text-sm font-mono font-semibold text-gray-900">
                  {user?.referralCode || '—'}
                </p>
              </div>
            </div>
          </div>

          {referrerInfo && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Referred By
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">
                    {referrerInfo.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">
                    {referrerInfo.email}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">
                    {referrerInfo.phone}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg ${
                    user?.kycStatus === 'Approved'
                      ? 'bg-success-light'
                      : 'bg-warning-light'
                  }`}
                >
                  <Shield
                    className={`w-5 h-5 ${
                      user?.kycStatus === 'Approved'
                        ? 'text-success'
                        : 'text-warning'
                    }`}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-600">KYC Status</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {user?.kycStatus || 'Pending'}
                  </p>
                </div>
              </div>
              {user?.kycStatus !== 'Approved' && (
                <button
                  onClick={() => navigate('/kyc')}
                  className="text-primary text-sm font-semibold"
                >
                  Complete
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Beneficiary (BANK or UPI) */}
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Beneficiary</h3>
            <button
              onClick={() => {
                setShowForm((prev) => !prev);
                if (beneficiaries.length === 0 && !showForm) {
                  setForm(initialForm);
                  setDocFiles(initialDocFilesState);
                  setDocProgress(initialDocProgressState);
                  Object.values(docPreviews).forEach((url) => {
                    try {
                      URL.revokeObjectURL(url);
                      docObjectUrlsRef.current.delete(url);
                    } catch (e) {}
                  });
                  setDocPreviews({});
                }
              }}
              className="text-primary text-sm font-semibold"
            >
              {showForm ? 'Cancel' : beneficiaries.length > 0 ? 'Manage' : 'Add'}
            </button>
          </div>

          {/* Form */}
          {showForm ? (
            <div className="space-y-3">
              {/* Type selector */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="type"
                    value="BANK"
                    checked={form.type === 'BANK'}
                    onChange={() => setForm({ ...form, type: 'BANK' })}
                    disabled={saving}
                  />
                  <span className="text-sm">Bank</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="type"
                    value="UPI"
                    checked={form.type === 'UPI'}
                    onChange={() => setForm({ ...form, type: 'UPI' })}
                    disabled={saving}
                  />
                  <span className="text-sm">UPI</span>
                </label>
              </div>

              {/* Shared: Name */}
              <input
                ref={nameRef}
                type="text"
                placeholder={
                  form.type === 'BANK'
                    ? 'Account Holder Name'
                    : 'Name (UPI)'
                }
                value={form.accountHolderName}
                onChange={(e) =>
                  setForm({ ...form, accountHolderName: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                disabled={saving}
              />

              {/* BANK fields */}
              {form.type === 'BANK' && (
                <>
                  <input
                    ref={bankNameRef}
                    type="text"
                    placeholder="Bank Name"
                    value={form.bankName}
                    onChange={(e) =>
                      setForm({ ...form, bankName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    disabled={saving}
                  />
                  <input
                    ref={bankBranchRef}
                    type="text"
                    placeholder="Branch"
                    value={form.bankBranch}
                    onChange={(e) =>
                      setForm({ ...form, bankBranch: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    disabled={saving}
                  />
                  <input
                    ref={accountNumberRef}
                    type="text"
                    placeholder="Account Number"
                    value={form.accountNumber}
                    onChange={(e) =>
                      setForm({ ...form, accountNumber: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    disabled={saving}
                  />
                  <input
                    ref={ifscRef}
                    type="text"
                    placeholder="IFSC Code"
                    value={form.ifscCode}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        ifscCode: e.target.value.toUpperCase(),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    disabled={saving}
                  />

                  {/* BANK documents */}
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">
                      Upload Bank Documents
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <DocUploadCard
                        keyName="bank_passbook"
                        label="Bank Passbook"
                      />
                      <DocUploadCard
                        keyName="cancelled_cheque"
                        label="Cancelled Cheque"
                      />
                      <DocUploadCard
                        keyName="bank_statement"
                        label="Bank Statement"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* UPI fields + doc */}
              {form.type === 'UPI' && (
                <>
                  <input
                    ref={upiRef}
                    type="text"
                    placeholder="UPI ID (e.g. name@bank)"
                    value={form.upiId}
                    onChange={(e) =>
                      setForm({ ...form, upiId: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    disabled={saving}
                  />

                  <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">
                      UPI Proof (screenshot / QR) 
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <DocUploadCard
                        keyName="upi_screenshot"
                        label="UPI Screenshot / QR"
                      />
                    </div>
                  </div>
                </>
              )}

              <button
                onClick={handleSave}
                className="w-full bg-primary text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
                disabled={saving}
              >
                <Save className="w-5 h-5" /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : (
            // Display saved beneficiaries
            <>
              {loading ? (
                <p className="text-sm text-gray-600">Loading...</p>
              ) : beneficiaries.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No beneficiary added yet
                </p>
              ) : (
                <div className="space-y-3">
                  {/* UPI group */}
                  {beneficiaries.filter((b) => b.type === 'UPI').length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 mb-2">
                        UPI
                      </h4>
                      <div className="space-y-2">
                        {beneficiaries
                          .filter((b) => b.type === 'UPI')
                          .map((b, i) => (
                            <div
                              key={b.bankdetailId || b.upiId || `upi-${i}`}
                              className="p-3 border rounded-md"
                            >
                              <p className="text-sm">
                                <span className="text-gray-600">Name:</span>{' '}
                                <span className="font-semibold">
                                  {b.accountHolderName || '—'}
                                </span>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-600">UPI ID:</span>{' '}
                                <span className="font-mono font-semibold">
                                  {b.upiId || '—'}
                                </span>
                              </p>
                              <p className="text-[11px] text-gray-500 mt-1">
                                Status: {b.status || 'Pending'}{' '}
                                {b.verified ? '(Verified)' : ''}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Bank group */}
                  {beneficiaries.filter((b) => b.type === 'BANK').length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 mb-2">
                        Bank Accounts
                      </h4>
                      <div className="space-y-2">
                        {beneficiaries
                          .filter((b) => b.type === 'BANK')
                          .map((b, i) => (
                            <div
                              key={
                                b.bankdetailId ||
                                b.accountNumber ||
                                `bank-${i}`
                              }
                              className="p-3 border rounded-md"
                            >
                              <p className="text-sm">
                                <span className="text-gray-600">Holder:</span>{' '}
                                <span className="font-semibold">
                                  {b.accountHolderName || '—'}
                                </span>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-600">Bank:</span>{' '}
                                <span className="font-semibold">
                                  {b.bankName || '—'}
                                </span>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-600">
                                  Account:
                                </span>{' '}
                                <span className="font-mono font-semibold">
                                  {b.accountNumber || '—'}
                                </span>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-600">IFSC:</span>{' '}
                                <span className="font-mono font-semibold">
                                  {b.ifscCode || '—'}
                                </span>
                              </p>
                              <p className="text-[11px] text-gray-500 mt-1">
                                Status: {b.status || 'Pending'}{' '}
                                {b.verified ? '(Verified)' : ''}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={() => navigate('/kyc')}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-gray-700" />
              <span className="font-semibold text-gray-900">
                KYC Verification
              </span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>

          <button
            onClick={() => navigate('/support')}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <Headphones className="w-5 h-5 text-gray-700" />
              <span className="font-semibold text-gray-900">
                Support & Tickets
              </span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full bg-error text-white font-semibold py-4 rounded-lg flex items-center justify-center gap-2"
        >
          <LogOut className="w-5 h-5" /> Logout
        </button>
      </div>
    </MobileLayout>
  );
};

export default Profile;
